'use client';

import React, { useState, useEffect } from 'react';
import ReactPhoneNumberInput, { Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { cn } from '@/lib/utils';

interface PhoneInputProps {
  value: string;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  error?: boolean;
  /** Limit the country dropdown (e.g. the served-country list). Detection
      falls back to the first entry when the visitor's country isn't in it. */
  countries?: Country[];
  /** false = national formatting with the calling code carried by the flag
      select (the hero look); default true keeps existing consumers intact. */
  international?: boolean;
  /** Full class override for the inner number input (skips the login-form
      default styling when provided). */
  numberInputClassName?: string;
  /** Full class override for the country select. */
  countrySelectClassName?: string;
}

export function PhoneInput({
  value,
  onChange,
  disabled = false,
  required = false,
  autoFocus = false,
  placeholder = 'Enter phone number',
  className,
  error = false,
  countries,
  international = true,
  numberInputClassName,
  countrySelectClassName,
}: PhoneInputProps) {
  const [defaultCountry, setDefaultCountry] = useState<Country>(countries?.[0] ?? 'US');

  useEffect(() => {
    // Auto-detect user's country from timezone or fetch from geolocation API
    const detectCountry = async () => {
      try {
        // Try to detect from timezone first (fast, no API call)
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Map common timezones to countries
        const timezoneCountryMap: Record<string, Country> = {
          // United States
          'America/New_York': 'US',
          'America/Chicago': 'US',
          'America/Denver': 'US',
          'America/Phoenix': 'US',
          'America/Los_Angeles': 'US',
          'America/Anchorage': 'US',
          'America/Honolulu': 'US',
          // Canada
          'America/Toronto': 'CA',
          'America/Vancouver': 'CA',
          'America/Edmonton': 'CA',
          'America/Winnipeg': 'CA',
          'America/Halifax': 'CA',
          'America/St_Johns': 'CA',
          'America/Regina': 'CA',
          'America/Whitehorse': 'CA',
          'America/Yellowknife': 'CA',
          'America/Iqaluit': 'CA',
          'America/Montreal': 'CA',
          // Other countries
          'America/Mexico_City': 'MX',
          'Europe/London': 'GB',
          'Europe/Paris': 'FR',
          'Europe/Berlin': 'DE',
          'Asia/Tokyo': 'JP',
          'Asia/Shanghai': 'CN',
          'Asia/Hong_Kong': 'HK',
          'Asia/Singapore': 'SG',
          'Australia/Sydney': 'AU',
        };

        // When the dropdown is limited, a detected country outside the list
        // must not win — react-phone-number-input would ignore it and the
        // visitor would see an empty flag.
        const accept = (c: Country | undefined): c is Country =>
          !!c && (!countries || countries.includes(c));

        const detectedCountry = timezoneCountryMap[timezone];
        console.log('[PhoneInput] User timezone:', timezone);
        console.log('[PhoneInput] Mapped country:', detectedCountry);

        if (accept(detectedCountry)) {
          console.log('[PhoneInput] Setting country from timezone:', detectedCountry);
          setDefaultCountry(detectedCountry);
          return;
        }

        // Fallback: Use free IP geolocation API
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(3000)
        });

        if (response.ok) {
          const data = await response.json();
          const ipCountry = data.country_code as Country | undefined;
          if (accept(ipCountry)) {
            console.log('[PhoneInput] Detected country from IP:', ipCountry);
            setDefaultCountry(ipCountry);
          }
        }
      } catch (error) {
        console.log('[PhoneInput] Could not detect country, using US as default');
        // Keep default as US if detection fails
      }
    };

    detectCountry();
    // `countries` is a stable module-level constant in every caller — re-running
    // detection (and the ipapi fetch) on re-render would be waste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ReactPhoneNumberInput
      international={international}
      // National mode exists to GUARANTEE a country — the lib's "International"
      // pseudo-option would reopen the type-without-country-code hole.
      addInternationalOption={international !== false}
      defaultCountry={defaultCountry}
      countries={countries}
      value={value}
      onChange={onChange}
      disabled={disabled}
      required={required}
      autoFocus={autoFocus}
      placeholder={placeholder}
      className={cn(
        'phone-input-container',
        className
      )}
      numberInputProps={{
        className: numberInputClassName ?? cn(
          'w-full px-3 py-2 border rounded-md shadow-sm',
          'text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800',
          'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500',
          error
            ? 'border-red-500 dark:border-red-600'
            : 'border-gray-300 dark:border-gray-600',
          disabled && 'opacity-50 cursor-not-allowed'
        )
      }}
      countrySelectProps={{
        className: countrySelectClassName ?? cn(
          'px-2 py-2 border rounded-l-md',
          'text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800',
          'border-gray-300 dark:border-gray-600',
          'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500',
          disabled && 'opacity-50 cursor-not-allowed'
        )
      }}
    />
  );
}
